import { Fragment, useMemo, useRef } from "react";

import {
  Box,
  Column,
  Row,
  SparkleIcon,
  TextWithTooltip,
} from "@hightouch/htui";

import type { VirtualItem } from "./use-virtual-items";
import { AI_SUGGESTIONS_GROUP_LABEL } from "./filter-popover";

type GroupLabelsProps<T extends { label: string }> = {
  items: VirtualItem<T>[];
};

export const GroupLabels = <T extends { label: string }>({
  items,
}: GroupLabelsProps<T>) => {
  const groupLabelsRef = useRef<Array<HTMLDivElement | null>>([]);

  const spaceBetweenGroupLabels = useMemo(() => {
    const result = {};
    let lastGroupIndex = 0;

    for (let index = 0; index < items.length; index++) {
      const item = items[index];

      // Get the difference in height between the last group label and the current group label
      if (item?.type === "group") {
        result[item.label] =
          index === 0 ? 0 : 48 * (index - lastGroupIndex - 1);
        lastGroupIndex = index;
      }
    }
    return result;
  }, [items]);

  return (
    <Column position="absolute" top={0} width="calc(100% - 8px)">
      {items.map((item, index) => {
        if (item.type === "group") {
          return (
            <Fragment key={`${item.label}-${index}`}>
              {/* Placeholder div that accounts for heights of options. Spaces out the labels. */}
              <Box
                bg="transparent"
                flexShrink={0}
                height={
                  // -1 to account for the group label height
                  `${spaceBetweenGroupLabels[item.label]}px`
                }
              />
              <Row
                // biome-ignore lint/suspicious/noAssignInExpressions: intentional assignment in expression
                ref={(el) => (groupLabelsRef.current[index] = el)}
                position="sticky"
                top={-2}
                flexShrink={0}
                align="center"
                gap={1}
                bg="white"
                height={`${item.height}px`}
                zIndex={1}
              >
                {item.label === AI_SUGGESTIONS_GROUP_LABEL && (
                  <Box
                    as={SparkleIcon}
                    fontSize="12px"
                    color="text.secondary"
                    flexShrink={0}
                  />
                )}
                <TextWithTooltip
                  color="text.secondary"
                  fontWeight="semibold"
                  size="sm"
                >
                  {item.label}
                </TextWithTooltip>
              </Row>
            </Fragment>
          );
        }

        return null;
      })}
    </Column>
  );
};
